import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MIN_SCORE, passwordMatch } from '../../../utils/validators';
import { debounceTime, skip } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../shared-components/ng-login/auth/auth.service';
import { CustomSnackbarService } from '../../../services/custom-snackbar.service';
import { Customer } from '../../../models/customer';
import { CustomerService } from '../../../services/customer.service';
import { TranslateService } from '@ngx-translate/core';
import { UserPassword } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { VerticalSelectorService } from '../../../services/vertical-selector.service';
import { getStorageObject } from '../../../utils/storage-manager';
import { securityDomain } from '../../main/main.component';

/**
 * Component selector, template-form and styles
 */
@Component({
  selector: 'app-reset-password',
  styleUrls: ['./reset-password.component.scss'],
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnInit {
  private verticalSelectorService = inject(VerticalSelectorService);
  private builder = inject(FormBuilder);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private customerService = inject(CustomerService);
  private translate = inject(TranslateService);
  private snackBarService = inject(CustomSnackbarService);
  private route = inject(ActivatedRoute);
  /**
   * Reset password form
   */
  public resetPasswordForm!: FormGroup;

  /**
   * Flag that indicates if the form has been submitted at least once (used for validations)
   * @type {boolean}
   */
  public submittedOnce = false;
  /**
   * Flag that indicates if the form has been submitted correctlly
   * @type {boolean}
   */
  public submittedCorrect = false;
  /**
   * Min password score
   */
  minScore = MIN_SCORE;
  /**
   * password To check
   */
  public passwordToCheck = '';
  /**
   * Score password
   */
  scorePassword = 0;
  /**
   * Whether the token is valid or not
   */
  public validToken = true;
  /**
   * Reset password token
   */
  @Input() token = '';

  currentCustomer: Customer | null = getStorageObject('vertical');

  protected readonly securityDomain = securityDomain;

  passwordMinLength = 7;

  passwordRegex = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(.*[!@#$%^&*+=\\\\?\\-<>|(){}\'\\";:/€].*)');

  /**
   * Creates the reset password form fields and their validations
   */
  ngOnInit(): void {
    this.resetPasswordForm = this.builder.group(
      {
        password: ['', [
          Validators.required,
          Validators.minLength(this.passwordMinLength),
          Validators.pattern(this.passwordRegex),
        ]],
        repeatPassword: ['', Validators.required]
      },
      { validators: passwordMatch }
    );

    this.verticalSelectorService.setVertical(null);
    this.route.params.subscribe((params) => {
      this.customerService.getCustomer(params.subdomain).subscribe(customer => {
        this.currentCustomer = customer;
        this.verticalSelectorService.setVertical(customer);
        this.checkTranslation(params.language);
        this.token = params.token;
        this.checkToken(params.token);
      })
    });
  }

  /**
   * Method that checks if the provided token is valid or not
   * @param token Activate account token
   */
  checkToken(token: string): void {
    this.authService
      .checkResetPasswordToken(token)
      .pipe(skip(1))
      .subscribe((email) => {
        this.validToken = !!email;
      });
  }

  /**
   * Method called on the event 'onSubmit'. It submits the new passwords to the API and, if the response
   * is correct, emits an event
   */
  public resetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      this.submittedOnce = true;

      return;
    }
    this.snackBarService.present(this.translate.instant('Changing password'));
    this.userService.resetPasswordScoreWithToken(this.token, this.assignDataFormToUserPassword()).subscribe(() => {
      this.submittedCorrect = true;
      this.verticalSelectorService.setVertical(null)
    });
  }

  /**
   * Change the score
   */
  changeScore(score: number): void {
    this.scorePassword = score;
  }

  /**
   * Method to translate if it is necessary
   */
  checkTranslation(language: string): void {
    if (language && this.translate.currentLang !== language) {
      this.translate.use(language);
    }
  }

  /**
   * Set the password in a variable
   */
  getPasswordScore(): void {
    if (this.resetPasswordForm.get('password')?.value) {
      this.resetPasswordForm
        .get('password')
        ?.valueChanges.pipe(debounceTime(400))
        .subscribe(() => (this.passwordToCheck = this.resetPasswordForm.get('password')?.value));
    }
  }

  /**
   * Assign data form to User password
   */
  public assignDataFormToUserPassword(): UserPassword {
    return new UserPassword(
      this.resetPasswordForm.get('password')?.value,
      this.resetPasswordForm.get('password')?.value
    );
  }
}
